import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, KeyRound, Send } from "lucide-react";

import { API_BASE, formatApiErrorDetail } from "@/lib/api";
import { CATEGORY_LABELS, STATUS_LABELS, statusPillClass } from "./erLabels";

const publicApi = axios.create({ baseURL: API_BASE, timeout: 20000 });

export default function ErAnonymousPortal() {
  const navigate = useNavigate();
  const [caseNumber, setCaseNumber] = useState("");
  const [token, setToken] = useState("");
  const [caseData, setCaseData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const lookup = async () => {
    if (!caseNumber || !token) return;
    setBusy(true);
    try {
      const { data } = await publicApi.post("/er/token/lookup", {
        case_number: caseNumber,
        token,
      });
      setCaseData(data.case);
      setMessages(data.messages || []);
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      toast.error(detail);
      setCaseData(null);
      setMessages([]);
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await publicApi.post("/er/token/messages/send", {
        case_number: caseNumber,
        token,
        body: reply.trim(),
      });
      toast.success("Message sent.");
      setReply("");
      await lookup();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-sage-200 py-10 px-4">
      <div className="max-w-3xl mx-auto space-y-6" data-testid="er-anon-portal-page">
        <button
          onClick={() => navigate("/login")}
          className="inline-flex items-center text-forest-700 text-sm font-medium"
        >
          <ArrowLeft className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
          Back to sign-in
        </button>

        <div className="patria-card p-8">
          <p className="text-xs uppercase tracking-widest text-terracotta-500 font-medium">
            Patria Church — People Operations
          </p>
          <h1 className="font-serif text-forest-900 text-4xl mt-2" data-testid="er-anon-portal-headline">
            Check On An Anonymous Report
          </h1>
          <p className="mt-3 text-charcoal-soft max-w-2xl">
            Enter your case number and the token you saved when you submitted your report.
            You can also send new messages to People Operations without revealing your identity.
          </p>

          <section className="mt-8 space-y-4" data-testid="er-anon-portal-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="patria-label" htmlFor="cn">Case number</label>
                <input
                  id="cn"
                  data-testid="er-anon-portal-case"
                  className="patria-input mt-2 font-mono"
                  placeholder="ER-YYYY-NNNN"
                  value={caseNumber}
                  onChange={(e) => setCaseNumber(e.target.value)}
                />
              </div>
              <div>
                <label className="patria-label" htmlFor="tk">Token</label>
                <input
                  id="tk"
                  data-testid="er-anon-portal-token"
                  className="patria-input mt-2 font-mono"
                  placeholder="XXXXXXXX-XXXX-XXXXXXXX-XXXX"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                />
              </div>
            </div>
            <div className="flex justify-end">
              <button
                onClick={lookup}
                disabled={!caseNumber || !token || busy}
                className="patria-btn-primary"
                data-testid="er-anon-portal-lookup"
              >
                <KeyRound className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
                {busy && !caseData ? "Looking up…" : "Look up my report"}
              </button>
            </div>
          </section>

          {caseData && (
            <section className="mt-10 space-y-6" data-testid="er-anon-portal-detail">
              <div className="patria-card p-5 border-l-4 border-forest-600">
                <p className="text-xs uppercase tracking-widest text-charcoal-muted">
                  {caseData.case_number}
                </p>
                <h3 className="font-serif text-2xl text-forest-900 mt-1">
                  {CATEGORY_LABELS[caseData.category] || caseData.category}
                </h3>
                <span
                  className={`inline-flex items-center rounded-full border px-2.5 py-0.5 mt-2 text-xs ${statusPillClass(caseData.status)}`}
                  data-testid="er-anon-portal-status"
                >
                  {STATUS_LABELS[caseData.status] || caseData.status}
                </span>
              </div>

              <div>
                <h4 className="font-serif text-xl text-forest-900">Messages With People Operations</h4>
                <ul className="mt-3 space-y-3">
                  {messages.length === 0 && (
                    <li className="patria-card p-4 text-sm text-charcoal-muted italic">
                      No messages yet. When People Operations replies, it will appear here.
                    </li>
                  )}
                  {messages.map((m) => (
                    <li
                      key={m.id}
                      className={`patria-card p-4 ${m.sender_type === "po" ? "border-l-4 border-terracotta-500" : ""}`}
                      data-testid={`er-anon-portal-msg-${m.id}`}
                    >
                      <p className="text-xs uppercase tracking-widest text-charcoal-muted">
                        {m.sender_type === "po" ? "People Operations" : "You (anonymous)"} — {new Date(m.created_at).toLocaleString()}
                      </p>
                      <p className="mt-2 text-charcoal whitespace-pre-line">{m.body}</p>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <label className="patria-label">Send a message</label>
                <textarea
                  className="patria-input mt-2"
                  rows={4}
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Share what you'd like People Operations to know. Your identity remains anonymous."
                  data-testid="er-anon-portal-reply"
                />
                <div className="mt-3 flex justify-end">
                  <button
                    onClick={send}
                    disabled={!reply.trim() || busy}
                    className="patria-btn-primary"
                    data-testid="er-anon-portal-send"
                  >
                    <Send className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
                    Send message
                  </button>
                </div>
              </div>
            </section>
          )}
        </div>

        <p className="text-xs text-charcoal-muted text-center italic">
          Don&apos;t have a report yet?{" "}
          <Link to="/er/anonymous" className="patria-link">Submit one here</Link>.
        </p>
      </div>
    </div>
  );
}
